import { Component, inject, OnDestroy, OnInit, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { GlobalVariablesService } from '../../global-variables.service';
import { isLightScreen } from '../../../studio-nav/screen-tone';
import { ShowHighlightNgService } from './show-highlight-ng.service';
import { targetSelector } from './show-highlight.service';
import type { ActiveHighlight, ShowTarget } from './show-highlight.service';

/** The element the ring is drawn on; the show tool checks the same lookup before answering. */
export function findTargetElement(target: ShowTarget): HTMLElement | null {
  return document.querySelector(targetSelector(target));
}

function targetDisplayName(target: ShowTarget): string {
  const el = findTargetElement(target);
  if (target.kind === 'field') return target.id;
  if (target.kind === 'component' && el) {
    const name = el.getAttribute('data-mcp-component-name');
    return name || target.id;
  }
  if (target.kind === 'area') return target.id.charAt(0).toUpperCase() + target.id.slice(1);
  return target.id;
}

// The note's size isn't known until it has drawn, so this is the room it is given.
// Was (2026-10-30), until a note drew 307 wide and sat on the button it asked to be pressed:
// const NOTE_WIDTH = 260;
// const NOTE_HEIGHT = 80;
// max-w-xs stops it at 320, so that is the width. A long note runs to about four lines.
const NOTE_WIDTH = 320;
const NOTE_HEIGHT = 120;
const NOTE_GAP = 12;
const SCREEN_MARGIN = 8;

// The target can be found a moment before its panel has finished laying out, so it is checked for half a second.
// No longer than that: after this the scrolling is the builder's, and the ring just follows.
const REVEAL_CHECKS = 5;

/** The overlay ignores the pointer, so a point here finds whatever is underneath it. */
function noteWouldCoverABox(top: number, left: number): boolean {
  // Steps smaller than the slimmest box, so one can't sit between two samples.
  for (let x = left; x <= left + NOTE_WIDTH; x += 40) {
    for (let y = top; y <= top + NOTE_HEIGHT; y += 16) {
      // Was (2026-10-30), until a note sat on the warning it asked the builder to act on: closest('input, textarea, select')
      if (document.elementFromPoint(x, y)?.closest('input, textarea, select, [data-mcp-keep-clear]')) return true;
    }
  }
  return false;
}

interface RingBox { top: number; left: number; width: number; height: number }

interface NoteSpot { top: number; left: number }

/** Whether the builder can see the element now: in the window, and not clipped by a scroller around it. */
function isOnScreen(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  const x = r.left + r.width / 2;
  const y = r.top + r.height / 2;
  if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return false;
  return document.elementsFromPoint(x, y).some((at) => at === el || el.contains(at));
}

function inTheWindow(spot: NoteSpot): boolean {
  return spot.top >= SCREEN_MARGIN && spot.left >= SCREEN_MARGIN
    && spot.top + NOTE_HEIGHT <= window.innerHeight - SCREEN_MARGIN
    && spot.left + NOTE_WIDTH <= window.innerWidth - SCREEN_MARGIN;
}

function clampedLeft(left: number): number {
  return Math.max(SCREEN_MARGIN, Math.min(left, window.innerWidth - NOTE_WIDTH - SCREEN_MARGIN));
}

function clampedTop(top: number): number {
  return Math.max(SCREEN_MARGIN, Math.min(top, window.innerHeight - NOTE_HEIGHT - SCREEN_MARGIN));
}

function noteOnRing(spot: NoteSpot, ring: RingBox): boolean {
  return spot.left < ring.left + ring.width && ring.left < spot.left + NOTE_WIDTH
    && spot.top < ring.top + ring.height && ring.top < spot.top + NOTE_HEIGHT;
}

/**
 * Under the ring unless that is in the way. A mark to press stays up while the builder works, so its note
 * has to leave both the button and any box they may want to change clear, and stay in the window.
 */
function noteSpot(ring: RingBox, sticky: boolean): NoteSpot {
  const under = { top: ring.top + ring.height + NOTE_GAP, left: clampedLeft(ring.left) };
  if (!sticky) return under;
  const spots: NoteSpot[] = [
    under,
    { top: ring.top - NOTE_HEIGHT - NOTE_GAP, left: clampedLeft(ring.left) },
    { top: clampedTop(ring.top), left: ring.left - NOTE_WIDTH - NOTE_GAP },
    { top: clampedTop(ring.top), left: ring.left + ring.width + NOTE_GAP },
    // On a narrow window or a dense form nothing beside the button may be free; the ring still marks it from here.
    { top: window.innerHeight - NOTE_HEIGHT - SCREEN_MARGIN, left: clampedLeft(ring.left) },
    { top: SCREEN_MARGIN, left: clampedLeft(ring.left) },
    { top: window.innerHeight - NOTE_HEIGHT - SCREEN_MARGIN, left: SCREEN_MARGIN },
    { top: window.innerHeight - NOTE_HEIGHT - SCREEN_MARGIN, left: window.innerWidth - NOTE_WIDTH - SCREEN_MARGIN },
    { top: SCREEN_MARGIN, left: SCREEN_MARGIN },
    { top: SCREEN_MARGIN, left: window.innerWidth - NOTE_WIDTH - SCREEN_MARGIN },
  ];
  const clear = spots.filter((spot) => inTheWindow(spot) && !noteOnRing(spot, ring));
  return clear.find((spot) => !noteWouldCoverABox(spot.top, spot.left)) ?? clear[0] ?? under;
}

@Component({
  selector: 'mcp-show-highlight-overlay',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (ring && highlight) {
      <!-- A mark to press has no backdrop: the builder's click has to reach the button underneath. -->
      <div class="fixed inset-0 z-[9998] pointer-events-none" (click)="dismiss()"
        [style.pointer-events]="highlight.sticky ? 'none' : 'auto'"
        [style.background]="highlight.sticky ? 'transparent' : 'rgba(0,0,0,0.15)'">
        <div class="absolute border-2 border-blue-500 rounded-lg pointer-events-none"
          [style.top.px]="ring.top - 4"
          [style.left.px]="ring.left - 4"
          [style.width.px]="ring.width + 8"
          [style.height.px]="ring.height + 8"
          style="box-shadow: 0 0 0 4px rgba(59,130,246,0.25), 0 0 20px rgba(59,130,246,0.15); transition: all 0.2s ease;">
        </div>
        <div class="absolute rounded-lg shadow-xl px-4 py-2 max-w-xs pointer-events-none" [ngClass]="dark ? 'bg-zinc-800' : 'bg-white'"
          [style.top.px]="noteTop"
          [style.left.px]="noteLeft">
          <div class="text-xs uppercase tracking-wider text-blue-500 font-semibold">
            {{ highlight.target.kind }}
          </div>
          <div class="text-sm font-medium" [ngClass]="dark ? 'text-zinc-100' : 'text-zinc-900'">
            {{ displayName }}
          </div>
          @if (highlight.note) {
            <div class="text-xs mt-0.5" [ngClass]="dark ? 'text-zinc-400' : 'text-zinc-500'">{{ highlight.note }}</div>
          }
          @if (!highlight.sticky) {
            <div class="text-[10px] text-zinc-400 mt-1">Click anywhere to dismiss</div>
          }
        </div>
      </div>
    }
  `,
})
export class ShowHighlightOverlayComponent implements OnInit, OnDestroy {
  readonly highlights = inject(ShowHighlightNgService);
  private zone = inject(NgZone);
  private router = inject(Router);
  private globals = inject(GlobalVariablesService);

  // Tailwind's dark: follows the OS; the card sits on the editor's screen, so it takes the editor's tone.
  get dark(): boolean {
    return !isLightScreen(this.router.url, this.globals.pageThemeDark);
  }

  ring: RingBox | null = null;
  highlight: ActiveHighlight | null = null;
  displayName = '';
  noteTop = 0;
  noteLeft = 0;

  // The ring follows its target as the page scrolls or reflows, which no event reports, so it polls;
  // only while a highlight is up: a few seconds for a show, up to the wait's limit for a mark to press.
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private stopListening: (() => void) | null = null;
  private revealing: ActiveHighlight | null = null;
  private revealChecksLeft = 0;

  ngOnInit(): void {
    this.stopListening = this.highlights.onChange(() => this.highlightChanged());
    this.highlightChanged();
  }

  ngOnDestroy(): void {
    this.stopListening?.();
    this.stopListening = null;
    this.stopPolling();
  }

  private highlightChanged(): void {
    if (this.highlights.activeHighlight) {
      if (this.pollTimer === null) {
        this.zone.runOutsideAngular(() => {
          this.pollTimer = setInterval(() => this.sync(), 100);
        });
      }
    } else {
      this.stopPolling();
    }
    this.sync();
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private sync(): void {
    const h = this.highlights.activeHighlight;
    if (!h) {
      if (this.ring) {
        this.zone.run(() => {
          this.ring = null;
          this.highlight = null;
        });
      }
      return;
    }

    const el = findTargetElement(h.target);
    if (!el) {
      if (this.ring) {
        this.zone.run(() => {
          this.ring = null;
          this.highlight = null;
        });
      }
      return;
    }

    this.reveal(h, el);

    const rect = el.getBoundingClientRect();
    const newRing: RingBox = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };

    const changed = !this.ring ||
      this.ring.top !== newRing.top || this.ring.left !== newRing.left ||
      this.ring.width !== newRing.width || this.ring.height !== newRing.height ||
      this.highlight !== h;

    if (changed) {
      this.zone.run(() => {
        this.ring = newRing;
        this.highlight = h;
        this.displayName = targetDisplayName(h.target);
        // Was (2026-10-30), until the note could cover the button it pointed at. noteSpot now weighs both:
        // this.noteTop = newRing.top + newRing.height + 12;
        // this.noteLeft = Math.max(8, Math.min(newRing.left, window.innerWidth - 260));
        // if (h.sticky && noteWouldCoverABox(this.noteTop, this.noteLeft)) {
        //   this.noteTop = newRing.top;
        //   this.noteLeft = Math.max(8, newRing.left - NOTE_WIDTH - 12);
        // }
        const spot = noteSpot(newRing, !!h.sticky);
        this.noteTop = spot.top;
        this.noteLeft = spot.left;
      });
    }
  }

  // Every wait on a press has to start with the button in sight, on any screen: a ring below the fold looks like a hang.
  private reveal(h: ActiveHighlight, el: HTMLElement): void {
    if (this.revealing !== h) {
      this.revealing = h;
      this.revealChecksLeft = REVEAL_CHECKS;
    }
    if (this.revealChecksLeft <= 0) return;
    this.revealChecksLeft--;
    if (!isOnScreen(el)) el.scrollIntoView({ block: 'center' });
  }

  dismiss(): void {
    this.highlights.clear();
    this.ring = null;
    this.highlight = null;
  }
}
